// First, install pako: npm install pako
import pako from 'pako';

/**
 * Compression utilities for IndexedDB storage
 */
class CompressionHelper {
    static compress(data) {
        try {
            const jsonString = JSON.stringify(data);

            const compressed = pako.deflate(jsonString);

            // Convert Uint8Array to base64 without spread operator
            let binary = '';
            const bytes = new Uint8Array(compressed);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            const base64 = btoa(binary);

            return {
                _compressed: true,
                _data: base64
            };
        } catch (error) {
            console.error('Compression failed:', error);
            throw error;
        }
    }

    static decompress(storedData) {
        // Check if it's compressed data
        if (storedData && typeof storedData === 'object' && storedData._compressed === true) {
            try {
                // Decode base64 back to Uint8Array
                const binaryString = atob(storedData._data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const decompressed = pako.inflate(bytes, { to: 'string' });
                return JSON.parse(decompressed);
            } catch (error) {
                console.error('Decompression failed:', error);
                return null;
            }
        }

        // Return as-is if not compressed
        return storedData;
    }

    static shouldCompress(value) {
        try {
            const jsonString = JSON.stringify(value);
            const sizeInBytes = new Blob([jsonString]).size;
            return sizeInBytes > 1024; // Compress if larger than 1KB
        } catch {
            return false;
        }
    }

    static getCompressionStats(original, compressedBase64) {
        const originalSize = new Blob([JSON.stringify(original)]).size;
        const compressedSize = compressedBase64.length;
        const savedBytes = originalSize - compressedSize;
        const ratio = ((savedBytes / originalSize) * 100).toFixed(2);

        return {
            originalSize,
            compressedSize,
            savedBytes,
            ratio: parseFloat(ratio)
        };
    }
}

/**
 * A robust IndexedDB wrapper with built-in compression for high-performance storage.
 * Automatically compresses large text data to save storage space on mobile devices.
 */
class IndexedDBStorage {
    constructor(options = {}) {
        this.dbName = options.dbName || 'pappus-cache-db';
        this.storeName = options.storeName || 'cache';
        // Métadonnées légères de chaque entrée (non compressées), lues seules au démarrage
        // pour ne pas décompresser tout le cache
        this.metaStoreName = options.metaStoreName || 'meta';
        this.version = options.version || 2;
        this.enableCompression = options.enableCompression !== false;
        this.compressionThreshold = options.compressionThreshold || 1024;
        this.db = null;
        this.initPromise = null;

        // Enhanced batching system
        this.pendingWrites = new Map();
        this.writeTimeout = null;
        this.BATCH_DELAY = 10;
        this.MAX_BATCH_SIZE = 500;
        this.isFlushInProgress = false;

        // Performance monitoring with compression stats
        this.stats = {
            totalWrites: 0,
            totalFlushes: 0,
            averageFlushTime: 0,
            lastFlushSize: 0,
            compressionStats: {
                totalCompressed: 0,
                totalOriginalSize: 0,
                totalCompressedSize: 0,
                averageRatio: 0
            }
        };
    }

    async init() {
        if (this.db) return this.db;
        if (this.initPromise) return this.initPromise;

        this.initPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName);
                }
                if (!db.objectStoreNames.contains(this.metaStoreName)) {
                    db.createObjectStore(this.metaStoreName);
                }
            };

            // Un autre onglet garde une connexion à l'ancienne version : la mise à niveau attend sa fermeture
            request.onblocked = () => {
                console.warn('IndexedDB upgrade blocked: close the other Pappus tabs.');
            };

            request.onsuccess = (event) => {
                this.db = event.target.result;
                this.db.onclose = () => {
                    console.warn('IndexedDB connection closed unexpectedly.');
                    this.db = null;
                    this.initPromise = null;
                };
                // Laisser passer la mise à niveau demandée par un onglet plus récent
                this.db.onversionchange = () => {
                    this.db.close();
                    this.db = null;
                    this.initPromise = null;
                };
                resolve(this.db);
            };

            request.onerror = () => {
                console.error('IndexedDB error:', request.error);
                this.initPromise = null;
                reject(request.error);
            };
        });

        return this.initPromise;
    }

    get pendingWritesCount() {
        return this.pendingWrites.size;
    }

    get hasPendingWrites() {
        return this.pendingWrites.size > 0;
    }

    /**
     * Prepares data for storage with sanitization and optional compression
     */
    prepareForStorage(key, value) {
        try {
            // Step 1: Remove reactivity and circular refs
            const plainValue = JSON.parse(JSON.stringify(value));

            let shouldCompress = false
            try {
                shouldCompress = CompressionHelper.shouldCompress(plainValue)
            }catch(e) {
                console.warn(`Cannot evaluate compression ${key}: ${value}`)
            }

            // Step 2: Try to compress
            if (this.enableCompression && shouldCompress) {
                try {
                    const compressed = CompressionHelper.compress(plainValue);

                    if (compressed._compressed) {
                        const stats = CompressionHelper.getCompressionStats(plainValue, compressed._data);
                        this.updateCompressionStats(stats);
                    }

                    return compressed;
                } catch (compressionError) {
                    console.warn(`Compression failed for key "${key}":`, compressionError.message);
                    console.debug('Problematic value:', plainValue);
                    // Return uncompressed if compression fails
                    return plainValue;
                }
            }

            return plainValue;
        } catch (error) {
            console.error(`Failed to prepare data for key "${key}":`, error);
            throw error;
        }
    }
    /**
     * Updates compression statistics
     */
    updateCompressionStats(stats) {
        const cs = this.stats.compressionStats;
        cs.totalCompressed++;
        cs.totalOriginalSize += stats.originalSize;
        cs.totalCompressedSize += stats.compressedSize;
        if (cs.totalOriginalSize > 0) {
            cs.averageRatio = ((cs.totalOriginalSize - cs.totalCompressedSize) / cs.totalOriginalSize * 100);
        }
    }

    /**
     * Queues an item to be written to the database with compression.
     * `meta` (optionnel) est écrit tel quel dans le store des métadonnées, dans la même transaction.
     */
    async setItem(key, value, meta = null) {
        await this.init();

        let preparedValue;
        try {
            preparedValue = this.prepareForStorage(key, value);
        } catch (error) {
            console.error(`setItem failed for key "${key}":`, error);
            return Promise.reject(error);
        }

        return new Promise((resolve, reject) => {
            this.pendingWrites.set(key, { value: preparedValue, meta, resolve, reject });

            if (this.pendingWrites.size >= this.MAX_BATCH_SIZE) {
                this.flush();
            } else if (!this.writeTimeout) {
                this.writeTimeout = setTimeout(() => this.flushWrites(), this.BATCH_DELAY);
            }
        });
    }

    /**
     * Optimized bulk insert with compression
     * entries : [key, value, meta?]
     */
    async setBulk(entries) {
        if (entries.length === 0) return [];

        let preparedEntries;
        try {
            preparedEntries = entries.map(([key, value, meta]) => {
                const prepared = this.prepareForStorage(key, value);
                return [key, prepared, meta];
            });
        } catch (error) {
            console.error('Failed to prepare bulk entries:', error);
            return [{ success: false, count: 0, error: `Bulk preparation failed: ${error.message}` }];
        }

        const CHUNK_SIZE = 200;
        const results = [];

        for (let i = 0; i < preparedEntries.length; i += CHUNK_SIZE) {
            const chunk = preparedEntries.slice(i, i + CHUNK_SIZE);
            try {
                await this.processBulkChunk(chunk);
                results.push({ success: true, count: chunk.length });

                if (i + CHUNK_SIZE < preparedEntries.length) {
                    await new Promise(resolve => setTimeout(resolve, 10));
                }
            } catch (error) {
                console.error(`Chunk failed during bulk set:`, error);
                results.push({ success: false, count: 0, error: error.message });
            }
        }
        return results;
    }

    async processBulkChunk(entries) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName, this.metaStoreName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const metaStore = transaction.objectStore(this.metaStoreName);

            transaction.oncomplete = () => resolve();
            transaction.onerror = (event) => {
                reject(new Error(`Bulk transaction failed: ${event.target.error?.message || 'Unknown error'}`));
            };

            for (const [key, value, meta] of entries) {
                try {
                    const request = store.put(value, key);
                    request.onerror = (event) => {
                        console.error(`Failed to store bulk key ${key}:`, event.target.error);
                    };
                    if (meta) metaStore.put(meta, key);
                } catch (error) {
                    console.error(`Error putting bulk key ${key}:`, error);
                }
            }
        });
    }

    async flushWrites() {
        if (this.isFlushInProgress || this.pendingWrites.size === 0) {
            return;
        }

        this.isFlushInProgress = true;
        const flushStartTime = performance.now();
        const batchSize = this.pendingWrites.size;

        await this.init();
        const writes = new Map(this.pendingWrites);
        this.pendingWrites.clear();

        if (this.writeTimeout) {
            clearTimeout(this.writeTimeout);
            this.writeTimeout = null;
        }

        try {
            const transaction = this.db.transaction([this.storeName, this.metaStoreName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const metaStore = transaction.objectStore(this.metaStoreName);

            const transactionPromise = new Promise((resolve, reject) => {
                transaction.oncomplete = () => {
                    for (const { resolve: promiseResolve } of writes.values()) {
                        promiseResolve();
                    }
                    resolve();
                };
                transaction.onerror = (event) => reject(event.target.error || new Error('Transaction failed during flush'));
                transaction.onabort = () => reject(new Error('Transaction aborted during flush'));
            });

            for (const [key, { value, meta, reject }] of writes) {
                try {
                    const request = store.put(value, key);
                    request.onerror = (event) => {
                        console.error(`Store error for key "${key}":`, event.target.error);
                        reject(event.target.error);
                    };
                    if (meta) metaStore.put(meta, key);
                } catch (error) {
                    console.error(`Exception putting key "${key}":`, error);
                    console.error('Problematic value:', value);
                    reject(error);
                }
            }

            await transactionPromise;

            const flushDuration = performance.now() - flushStartTime;
            this.stats.totalFlushes++;
            this.stats.totalWrites += batchSize;
            this.stats.lastFlushSize = batchSize;
            this.stats.averageFlushTime = ((this.stats.averageFlushTime * (this.stats.totalFlushes - 1)) + flushDuration) / this.stats.totalFlushes;

        } catch (error) {
            console.error('Flush operation failed:', error);
            for (const { reject } of writes.values()) {
                reject(error);
            }
        } finally {
            this.isFlushInProgress = false;
            if (this.pendingWrites.size > 0 && !this.writeTimeout) {
                this.writeTimeout = setTimeout(() => this.flushWrites(), this.BATCH_DELAY);
            }
        }
    }

    /**
     * Retrieves and decompresses an item from the database
     */
    async getItem(key) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readonly');
            const request = transaction.objectStore(this.storeName).get(key);
            request.onsuccess = () => {
                const result = request.result;
                if (result === undefined) {
                    resolve(undefined);
                } else {
                    const decompressed = CompressionHelper.decompress(result);
                    resolve(decompressed);
                }
            };
            request.onerror = (event) => reject(event.target.error);
        });
    }

    /**
     * Retrieves multiple items with decompression
     */
    async getMultiple(keys) {
        if (keys.length === 0) return new Map();
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const results = new Map();
            transaction.oncomplete = () => resolve(results);
            transaction.onerror = (event) => reject(event.target.error);

            for (const key of keys) {
                const request = store.get(key);
                request.onsuccess = () => {
                    if (request.result !== undefined) {
                        const decompressed = CompressionHelper.decompress(request.result);
                        results.set(key, decompressed);
                    }
                };
            }
        });
    }

    async removeItem(key) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName, this.metaStoreName], 'readwrite');
            transaction.objectStore(this.storeName).delete(key);
            transaction.objectStore(this.metaStoreName).delete(key);
            transaction.oncomplete = () => resolve();
            transaction.onerror = (event) => reject(event.target.error);
        });
    }

    async clear() {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName, this.metaStoreName], 'readwrite');
            transaction.objectStore(this.metaStoreName).clear();
            const request = transaction.objectStore(this.storeName).clear();
            request.onsuccess = () => {
                // Reset compression stats
                this.stats.compressionStats = {
                    totalCompressed: 0,
                    totalOriginalSize: 0,
                    totalCompressedSize: 0,
                    averageRatio: 0
                };
                resolve();
            };
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async keys() {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readonly');
            const request = transaction.objectStore(this.storeName).getAllKeys();
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = (event) => reject(event.target.error);
        });
    }

    /**
     * Toutes les métadonnées, sans lire ni décompresser les entrées : Map key → meta
     */
    async getAllMeta() {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.metaStoreName], 'readonly');
            const store = transaction.objectStore(this.metaStoreName);
            const keysRequest = store.getAllKeys();
            const valuesRequest = store.getAll();
            transaction.oncomplete = () => {
                const keys = keysRequest.result || [];
                const values = valuesRequest.result || [];
                resolve(new Map(keys.map((key, i) => [key, values[i]])));
            };
            transaction.onerror = (event) => reject(event.target.error);
        });
    }

    /**
     * Écrit des métadonnées seules (reconstruction de l'index d'une base créée avant le store meta)
     */
    async setMetaBulk(entries) {
        if (entries.length === 0) return;
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.metaStoreName], 'readwrite');
            const store = transaction.objectStore(this.metaStoreName);
            for (const [key, meta] of entries) store.put(meta, key);
            transaction.oncomplete = () => resolve();
            transaction.onerror = (event) => reject(event.target.error);
        });
    }

    async flush() {
        if (this.writeTimeout) {
            clearTimeout(this.writeTimeout);
            this.writeTimeout = null;
        }
        if (this.hasPendingWrites) {
            await this.flushWrites();
        }
    }

    /**
     * Get detailed statistics including compression info
     */
    getStats() {
        const cs = this.stats.compressionStats;
        return {
            ...this.stats,
            pendingWrites: this.pendingWritesCount,
            hasPendingWrites: this.hasPendingWrites,
            isFlushInProgress: this.isFlushInProgress,
            compression: {
                enabled: this.enableCompression,
                totalCompressed: cs.totalCompressed,
                spaceSaved: `${((cs.totalOriginalSize - cs.totalCompressedSize) / 1024 / 1024).toFixed(2)} MB`,
                averageCompressionRatio: `${cs.averageRatio.toFixed(2)}%`,
                originalSize: `${(cs.totalOriginalSize / 1024 / 1024).toFixed(2)} MB`,
                compressedSize: `${(cs.totalCompressedSize / 1024 / 1024).toFixed(2)} MB`
            }
        };
    }

    /**
     * Check current storage usage
     */
    async getStorageInfo() {
        if (navigator.storage && navigator.storage.estimate) {
            const estimate = await navigator.storage.estimate();
            const usage = estimate.usage || 0;
            const quota = estimate.quota || 0;

            return {
                used: `${(usage / 1024 / 1024).toFixed(2)} MB`,
                quota: `${(quota / 1024 / 1024 / 1024).toFixed(2)} GB`,
                available: `${((quota - usage) / 1024 / 1024 / 1024).toFixed(2)} GB`,
                percentUsed: `${((usage / quota) * 100).toFixed(2)}%`
            };
        }
        return null;
    }
}

export default IndexedDBStorage;